<template>
  <div class="delivery-calendar">
    <template v-if="type === 'newOrder'">
      <CoreDropdown
        :mode="dropDownMode ? dropDownMode : `select`"
        :icon="`sprites_delivery-car`"
        :show-content="isShowDropdownContent"
        padded-content
        separate-click-out-side-action
        @clickButton="showDropdownContent"
        @clickOutSide="hideDropdownContentWithDropChanges"
      >
        <SelectDayPresentation
          :params="getCurrentDeliveryDate"
        />

        <template #content>
          <SelectDayCalendar
            v-if="showType === 'date'"
            :calendar-days="calendarDays"
            :current-delivery-date="currentDeliveryDate"
            :new-delivery-date="newDeliveryDate"
            @selectNewDate="selectNewDate"
          >
            <SelectedDayAndTimePresentation
              :new-delivery-date="newDeliveryDate"
              :active-type="`date`"
              @clickTime="showTimePicker"
            />
          </SelectDayCalendar>
          <SelectTime
            v-if="showType === 'time'"
            :time-intervals="getTimeIntervals"
            @autoSelectNewTimeInterval="autoSelectNewTimeInterval"
            @selectNewTimeInterval="selectNewTimeInterval"
          >
            <SelectedDayAndTimePresentation
              :new-delivery-date="newDeliveryDate"
              :active-type="`time`"
              @clickDate="showDatePicker"
            />
          </SelectTime>
        </template>
      </CoreDropdown>
    </template>
    <template v-else-if="type === 'deliveryInfo'">
      <SelectDayCalendar
        show-current-date
        show-current-delivery-date
        :can-change-day="canChangeDate"
        :sub-class="`o-select-day-calendar__delivery-info`"
        :calendar-days="calendarDays"
        :current-delivery-date="currentDeliveryDate"
        :new-delivery-date="newDeliveryDate"
        @selectNewDate="selectNewDate"
      >
        <SelectDatePresentation
          :new-delivery-date="currentDeliveryDate"
        />
      </SelectDayCalendar>
      <div class="delivery-calendar__separator" />
      <SelectTime2
        :can-change-time="canChangeDate"
        :time-intervals="getTimeIntervals"
        :new-delivery-date="newDeliveryDate"
        :current-delivery-date="currentDeliveryDate"
        :delivery="delivery"
        @autoSelectNewTimeInterval="autoSelectNewTimeInterval"
        @selectNewTimeInterval="selectNewTimeInterval"
      >
        <SelectTimePresentation
          :new-delivery-date="currentDeliveryDate"
        />
      </SelectTime2>
    </template>
    <template v-else-if="type === 'checkout'">
      <CoreDropdown
        :is-loading="isLoading"
        :disable="isLoading"
        :mode="dropDownMode ? dropDownMode : `select`"
        :show-content="isShowDropdownContent"
        separate-click-out-side-action
        @clickButton="showDropdownContent"
        @clickOutSide="hideDropdownContentWithDropChanges"
      >
        <template #default>
          <SelectDayPresentation2
            v-if="options?.datePresentation === 'newOrder'"
            :params="getCurrentDeliveryDate"
            :selected-day-params="selectedDayParams"
            :is-express-delivery-selected="isExpressDeliverySelected"
          />
          <SelectDatePresentation2
            v-else
            :params="currentDeliveryDate"
          />
        </template>
        <template #content>
          <SelectDayCalendar
            v-show="showType === 'date'"
            show-current-date
            show-current-delivery-date
            :can-change-day="canChangeDate"
            :sub-class="`o-select-day-calendar__delivery-info`"
            :calendar-days="calendarDays"
            :current-delivery-date="currentDeliveryDate"
            :new-delivery-date="newDeliveryDate"
            @selectNewDate="selectNewDate"
          >
            <SelectedDayAndTimePresentation2
              :new-delivery-date="newDeliveryDate"
              :active-type="`date`"
              :is-express-delivery-selected="isExpressDeliverySelected"
              @clickTime="showTimePicker"
            />
            <template #footer>
              <div
                v-if="hasIntervals"
                class="o-select-day-info"
                @click="$emit('showFaq')"
              >
                <div class="o-select-day-info__text">
                  Некоторые дни и интервалы могут быть недоступны из-за графика доставок
                </div>
                <div
                  class="o-select-day-info__info"
                >
                  <img
                    :src="require('@/assets/images/icons/info.svg')"
                    alt=""
                  >
                </div>
              </div>
            </template>
          </SelectDayCalendar>
          <SelectTime3
            v-show="showType === 'time'"
            :show-selected-time-interval="false"
            :can-change-time="canChangeDate"
            :time-intervals="getTimeIntervals"
            :time-intervals-plus-day="getTimeIntervalsPlusDay"
            :time-intervals-plus-day-day="getTimeIntervalsPlus2Day"
            :new-delivery-date="newDeliveryDate"
            :current-delivery-date="currentDeliveryDate"
            :sub-class="`select-time-2__checkout`"
            :count-of-days-in-delivery="countOfDaysInDelivery"
            :is-express-delivery-selected="isExpressDeliverySelected"
            @autoSelectNewTimeInterval="autoSelectNewTimeInterval"
            @selectNewTimeInterval="selectNewTimeInterval"
          >
            <SelectedDayAndTimePresentation2
              :new-delivery-date="newDeliveryDate"
              :active-type="`time`"
              :is-express-delivery-selected="isExpressDeliverySelected"
              @clickDate="showDatePicker"
            />
          </SelectTime3>
        </template>
      </CoreDropdown>
    </template>
  </div>
</template>

<script>

import CoreDropdown from '@/components/core/ui/CoreDropdownV2/CoreDropdown'

import SelectedDayAndTimePresentation from '@/components/core/lib/Calendar/SelectedDayAndTimePresentation'
import SelectedDayAndTimePresentation2 from '@/components/core/lib/Calendar/SelectedDayAndTimePresentation2'

import SelectDatePresentation from '@/components/core/lib/Calendar/SelectDatePresentation'
import SelectDatePresentation2 from '@/components/core/lib/Calendar/SelectDatePresentation2'

import SelectDayPresentation from '@/components/core/lib/Calendar/SelectDayPresentation'
import SelectDayPresentation2 from '@/components/core/lib/Calendar/SelectDayPresentation2'

import SelectTimePresentation from '@/components/core/lib/Calendar/SelectTimePresentation'

import SelectDayCalendar from '@/components/core/lib/Calendar/SelectDayCalendar'

import SelectTime from '@/components/core/lib/Calendar/SelectTime'
import SelectTime2 from '@/components/core/lib/Calendar/SelectTime2'
import SelectTime3 from '@/components/core/lib/Calendar/SelectTime3'

import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'DeliveryCalendar',

  components: {
    SelectDayPresentation,
    SelectDayPresentation2,
    SelectDayCalendar,
    SelectTime,
    SelectTime2,
    SelectTime3,
    SelectedDayAndTimePresentation,
    SelectedDayAndTimePresentation2,
    CoreDropdown,
    SelectDatePresentation,
    SelectDatePresentation2,
    SelectTimePresentation,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },

    dropDownMode: {
      type: String,
      default: null,
    },

    canChangeDate: {
      type: Boolean,
      default: true,
    },

    resetDate: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: 'newOrder',
    },

    options: {
      type: Object,
      default () {
        return {}
      },
    },

    deliveryDays: {
      type: Object,
      required: true,
    },

    selectedDayParams: {
      type: Object,
      default () {
        return null
      },
    },

    delivery: {
      type: Object,
      required: false,
      default () {
        return null
      },
    },

    hasIntervals: {
      type: Boolean,
      default: false,
    },

    countOfDaysInDelivery: {
      type: Number,
      default () {
        return null
      },
    },
  },

  emits: [
    'setDeliveryDateParams',
    'showFaq',
  ],

  data () {
    return {
      isShowDropdownContent: false,
      showType: '',
      calendarDays: [],
      newDeliveryDate: {
        date: '',
        timeFrom: '',
        timeTo: '',
        type: '',
      },
      currentDeliveryDate: {
        date: '',
        timeFrom: '',
        timeTo: '',
        type: '',
      },
    }
  },

  computed: {
    ...mapGetters('marketing', ['isExpressDeliverySelected']),

    getCurrentDeliveryDate () {
      return this.$date(this.currentDeliveryDate.date)
    },

    getTimeIntervals () {
      return this.deliveryDays[this.newDeliveryDate.date]
    },

    getTimeIntervalsPlusDay () {
      return this.deliveryDays[this.$date(this.newDeliveryDate.date).add(1, 'day').format('YYYY-MM-DD')]
    },

    getTimeIntervalsPlus2Day () {
      return this.deliveryDays[this.$date(this.newDeliveryDate.date).add(2, 'day').format('YYYY-MM-DD')]
    },
  },

  watch: {
    'deliveryDays' () {
      this.setDefaultParams()
    },

    'resetDate' () {
      this.setCurrentDate(this.selectedDayParams)
      this.setNewDeliveryDate(this.selectedDayParams)
    },
  },

  mounted () {
    this.setDefaultParams()
  },

  methods: {
    ...mapActions('marketing', ['setExpressDeliveryState']),

    showDropdownContent () {
      this.showDatePicker()
      this.isShowDropdownContent = true
    },

    hideDropdownContent () {
      this.isShowDropdownContent = false
    },

    hideDropdownContentWithDropChanges () {
      this.hideDropdownContent()
      this.setNewDeliveryDate(this.currentDeliveryDate)
    },

    setDefaultParams () {
      this.getWeeks()

      if (!this.selectedDayParams) {
        const params = this.getDeliveryDateParamsByPossibleDays()
        this.setCurrentDate(params)
        this.setNewDeliveryDate(params)
      } else {
        this.setCurrentDate(this.selectedDayParams)
        this.setNewDeliveryDate(this.selectedDayParams)
      }
    },

    hideDatePicker () {
      this.showType = ''
    },

    showDatePicker () {
      this.showType = 'date'
    },

    showTimePicker () {
      this.showType = 'time'
    },

    setCurrentDate (params) {
      this.currentDeliveryDate.date = params.date
      this.currentDeliveryDate.timeFrom = params.timeFrom
      this.currentDeliveryDate.timeTo = params.timeTo
      this.currentDeliveryDate.type = params.type
    },

    setNewDeliveryDate (params) {
      this.newDeliveryDate.date = params.date
      this.newDeliveryDate.timeFrom = params.timeFrom
      this.newDeliveryDate.timeTo = params.timeTo
      this.newDeliveryDate.type = params.type
    },

    isDeliveryDay (day) {
      return Boolean(Object.keys(this.deliveryDays).find((value) => value === day.format('YYYY-MM-DD')))
    },

    /**
     * @description Build week based on date of week
     */
    getWeek (date) {
      const startOfWeek = this.$date(date).startOf('isoWeek')
      const endOfWeek = this.$date(date).endOf('isoWeek')

      let day = startOfWeek
      while (day <= endOfWeek) {
        this.calendarDays.push({
          date: day.format('YYYY-MM-DD'),
          active: this.isDeliveryDay(day),
        })
        day = day.clone().add(1, 'd')
      }
    },

    /**
     * @description Builds weeks based on possible delivery dates. Adds 3rd week if last possible day is not in previous two
     */
    getWeeks () {
      this.calendarDays = []

      this.getWeek(Object.keys(this.deliveryDays)[0])

      this.getWeek(this.$date(this.calendarDays[this.calendarDays.length - 1].date).add(1, 'd'))

      const possibleDays = Object.keys(this.deliveryDays)

      if (!this.calendarDays.find((value) => value.date === possibleDays[possibleDays.length - 1])) {
        this.getWeek(this.$date(this.calendarDays[this.calendarDays.length - 1].date).add(1, 'd'))
      }
    },

    /**
     * @description Получаем параметры доставки из первого возможного дня доставки
     */
    getDeliveryDateParamsByPossibleDays () {
      const firstDeliveryKey = Object.keys(this.deliveryDays)[0]

      return this.getBaseParams(firstDeliveryKey)
    },

    getBaseParams (date) {
      const dateParams = this.deliveryDays[date]

      const {
        evening = [],
        morning = [],
      } = dateParams

      let interval = {}
      let type = ''

      if (morning && morning.length > 0) {
        interval = morning[0]
        type = 'morning'
      } else if (evening && evening.length > 0) {
        interval = evening[0]
        type = 'evening'
      }

      const {
        from = null,
        to = null,
      } = interval

      return {
        date: date,
        timeFrom: from,
        timeTo: to,
        type: type,
      }
    },

    selectNewDate (value) {
      const {
        date = null,
        active = false,
      } = value

      if (active) {
        const params = this.getBaseParams(date)
        this.setNewDeliveryDate(params)
        this.showTimePicker()

        if (this.type === 'deliveryInfo') {
          this.$emit('setDeliveryDateParams', this.newDeliveryDate)
        }
      }
    },

    /**
     * @description Автоматически выставляемые данные не должны инициировать закрытие дропдауна
     *
     * @param value
     */
    autoSelectNewTimeInterval (value) {
      this.setNewDeliveryDate({
        date: this.newDeliveryDate.date,
        timeFrom: value.from,
        timeTo: value.to,
        type: value.type,
      })
    },

    selectNewTimeInterval (value) {
      let deliveryDateParams = {
        date: this.newDeliveryDate.date,
        timeFrom: value.from,
        timeTo: value.to,
        type: value.type,
      }
      if (value.isExpressDelivery) {
        deliveryDateParams = this.getDeliveryDateParamsByPossibleDays()
      }

      this.setExpressDeliveryState(value.isExpressDelivery)

      this.setNewDeliveryDate(deliveryDateParams)

      this.$emit('setDeliveryDateParams', this.newDeliveryDate)

      if (this.type === 'newOrder' || this.type === 'checkout') {
        this.setCurrentDate(this.newDeliveryDate)
        this.hideDropdownContent()
      }
    },
  },
}
</script>

<style lang="scss">
$prefix: 'delivery-calendar';

.#{$prefix} {
  position: relative;
}

.#{$prefix}__separator {
  width: 100%;
  height: 1px;

  background: #CCF1D3;

  margin: 0.24rem 0 0.24rem 0;
}

.o-select-day-info {
  display: flex;
  align-items: center;
  margin: 0.16rem .16rem 0;
  padding: .12rem .16rem;
  border: 1px solid map-get($colors, 'grey_8');
  border-radius: .08rem;
  &__text {
    flex: 1 1 100%;
    margin-right: .08rem;
    @include typography('b3', 'medium');
    color: map-get($colors, 'grey_60');
  }
  &__info {
    width: 0.20rem;
    height: 0.20rem;
  }
}
</style>
